import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Icon, cn } from "@sarva/ui";
import { TOUR_STEPS } from "../steps";
import type { AiMoment, TourStep } from "../types";

/**
 * A presenter's guide that sits on top of the real screens.
 *
 * Started with `?tour` on any URL; Next and Back move through `TOUR_STEPS`, navigating to each
 * screen as its step opens. Nothing here reads or writes product data — it is a script with a
 * remote control, so it cannot make a demo do something the product does not.
 *
 * The step index is kept in sessionStorage so a reload (or an accidental click on a nav link)
 * resumes where the presenter was. It is a number, not PHI, and storage failing only means a
 * reload starts the tour again.
 */
const STEP_KEY = "sarva.tour.step";
const HIGHLIGHT = ["ring-2", "ring-primary", "ring-offset-4", "rounded-md"];

function readStep(): number | null {
  try {
    const raw = window.sessionStorage?.getItem?.(STEP_KEY);
    if (raw === null || raw === undefined) return null;
    const index = TOUR_STEPS.findIndex((step) => step.id === raw);
    return index === -1 ? 0 : index;
  } catch {
    return null;
  }
}

function writeStep(step: TourStep | null): void {
  try {
    if (step === null) window.sessionStorage?.removeItem?.(STEP_KEY);
    else window.sessionStorage?.setItem?.(STEP_KEY, step.id);
  } catch {
    // A blocked storage API costs resume-on-reload, nothing else.
  }
}

export function DemoTour() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [index, setIndex] = useState<number | null>(() =>
    params.has("tour") ? (readStep() ?? 0) : readStep(),
  );
  const [minimised, setMinimised] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  // `?tour` starts (or resumes) the tour, then leaves the URL so the address bar stays clean
  // for the audience.
  useEffect(() => {
    if (!params.has("tour")) return;
    setIndex((current) => current ?? 0);
    const next = new URLSearchParams(params);
    next.delete("tour");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const step = index === null ? null : (TOUR_STEPS[index] ?? null);

  useEffect(() => {
    writeStep(step ?? null);
    // A click-in step names the list it starts from in `to`; once the presenter has clicked
    // into a record (`match`), the tour must not drag them back to the list.
    if (
      step?.to !== undefined &&
      location.pathname !== step.to &&
      !(step.match?.test(location.pathname) ?? false)
    )
      navigate(step.to);
    // Navigation is driven by the step changing, not by the presenter wandering off to
    // another screen mid-step — so `location` is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Outline the element the step talks about, once the screen has rendered it.
  useEffect(() => {
    if (step?.highlight === undefined) return;
    let element: Element | null = null;
    const timer = window.setTimeout(() => {
      element = document.querySelector(step.highlight ?? "");
      if (element === null) return;
      element.classList.add(...HIGHLIGHT);
      element.scrollIntoView?.({ block: "center", behavior: "smooth" });
    }, 600);
    return () => {
      window.clearTimeout(timer);
      element?.classList.remove(...HIGHLIGHT);
    };
  }, [step, location.pathname]);

  const go = useCallback((delta: number) => {
    setListOpen(false);
    setIndex((current) =>
      current === null
        ? current
        : Math.min(TOUR_STEPS.length - 1, Math.max(0, current + delta)),
    );
  }, []);

  const exit = useCallback(() => {
    writeStep(null);
    setIndex(null);
  }, []);

  // → and ← drive the tour unless the presenter is typing into a field.
  useEffect(() => {
    if (step === null) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "ArrowRight") go(1);
      if (event.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, go]);

  const chapters = useMemo(() => {
    const seen = new Map<string, number>();
    TOUR_STEPS.forEach((s, i) => {
      if (!seen.has(s.chapter)) seen.set(s.chapter, i);
    });
    return [...seen.entries()];
  }, []);
  // Chapters holding a live AI demonstration, badged in the menu so they can be found mid-demo.
  const aiChapters = useMemo(
    () => new Set(TOUR_STEPS.filter((s) => s.ai !== undefined).map((s) => s.chapter)),
    [],
  );

  if (step === null || index === null) return null;

  const onTarget =
    step.match === undefined ? true : step.match.test(location.pathname);
  const last = index === TOUR_STEPS.length - 1;

  if (minimised) {
    return (
      <button
        type="button"
        onClick={() => setMinimised(false)}
        className="fixed bottom-5 right-5 z-drawer flex items-center gap-2 rounded-full border border-default bg-default px-4 py-2 text-sm font-medium text-default shadow-drawer hover:bg-hover"
      >
        <Icon name="sparkles" size={4} className="text-primary" />
        Demo · {index + 1}/{TOUR_STEPS.length}
      </button>
    );
  }

  return (
    <aside
      aria-label="Demo guide"
      className="fixed bottom-5 right-5 z-drawer flex max-h-[calc(100dvh-6rem)] w-[400px] flex-col overflow-hidden rounded-xl border border-default bg-default shadow-drawer"
    >
      <header className="flex items-center gap-2 border-b border-default px-4 py-2.5">
        <button
          type="button"
          onClick={() => setListOpen((open) => !open)}
          className="flex min-w-0 items-center gap-1.5 rounded-md text-xs font-semibold uppercase tracking-caps text-primary hover:underline"
          aria-expanded={listOpen}
        >
          <span className="truncate">{step.chapter}</span>
          <Icon name={listOpen ? "chevron-up" : "chevron-down"} size={4} />
        </button>
        <span className="ml-auto shrink-0 text-xs tabular text-secondary">
          {index + 1} / {TOUR_STEPS.length}
        </span>
        <button
          type="button"
          onClick={() => setMinimised(true)}
          aria-label="Minimise demo guide"
          className="rounded-md p-1 text-secondary hover:bg-hover hover:text-default"
        >
          <Icon name="minus" size={4} />
        </button>
        <button
          type="button"
          onClick={exit}
          aria-label="End demo"
          className="rounded-md p-1 text-secondary hover:bg-hover hover:text-default"
        >
          <Icon name="close" size={4} />
        </button>
      </header>

      <div className="h-1 bg-subtle">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${((index + 1) / TOUR_STEPS.length) * 100}%` }}
        />
      </div>

      {listOpen ? (
        <nav aria-label="Demo chapters" className="overflow-y-auto px-2 py-2">
          {chapters.map(([chapter, at]) => (
            <button
              key={chapter}
              type="button"
              onClick={() => {
                setListOpen(false);
                setIndex(at);
              }}
              className={cn(
                "block w-full rounded-md px-3 py-1.5 text-left text-sm hover:bg-hover",
                chapter === step.chapter
                  ? "font-semibold text-primary"
                  : "text-default",
              )}
            >
              <span className="flex items-center gap-2">
                {chapter}
                {aiChapters.has(chapter) && (
                  <span className="inline-flex items-center gap-1 rounded-sm bg-primary-weak px-1.5 text-xs font-medium text-primary">
                    <Icon name="sparkles" size={4} />
                    AI
                  </span>
                )}
              </span>
            </button>
          ))}
        </nav>
      ) : (
        <div className="flex flex-col gap-3 overflow-y-auto px-4 py-3">
          <h2 className="text-lg font-semibold tracking-tight text-default">
            {step.title}
          </h2>

          {step.doThis !== undefined && (
            <p
              className={cn(
                "flex items-start gap-2 rounded-md px-3 py-2 text-sm",
                onTarget
                  ? "bg-success-weak text-success"
                  : "bg-warning-weak text-warning",
              )}
            >
              <Icon
                name={onTarget ? "check-circle" : "arrow-right"}
                size={4}
                className="mt-0.5 shrink-0"
              />
              <span>
                {onTarget ? "You're on the right screen." : step.doThis}
              </span>
            </p>
          )}

          <ul className="flex flex-col gap-1.5 text-sm leading-relaxed text-default">
            {step.say.map((line) => (
              <li key={line} className="flex gap-2">
                <span
                  aria-hidden
                  className="mt-2 size-1.5 shrink-0 rounded-full bg-primary"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          {step.pointAt !== undefined && (
            <p className="text-sm text-secondary">
              <span className="font-medium text-default">Point at: </span>
              {step.pointAt}
            </p>
          )}

          {step.ai !== undefined && <AiMomentBox moment={step.ai} />}

          {step.connected !== undefined && (
            <p className="rounded-md border border-default bg-subtle px-3 py-2 text-sm text-default">
              <span className="font-medium">
                When connected to the practice's systems and the RCM platform:{" "}
              </span>
              {step.connected}
            </p>
          )}
        </div>
      )}

      <footer className="flex items-center gap-2 border-t border-default px-4 py-2.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => go(-1)}
          disabled={index === 0}
        >
          <Icon name="arrow-left" size={4} />
          Back
        </Button>
        <span className="ml-auto text-xs text-secondary">
          ← → keys work too
        </span>
        {last ? (
          <Button variant="primary" size="sm" onClick={exit}>
            Finish
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={() => go(1)}>
            Next
            <Icon name="arrow-right" size={4} />
          </Button>
        )}
      </footer>
    </aside>
  );
}

/** "Show it live": the clicks, the text to paste, and what the audience will see. */
function AiMomentBox({ moment }: { moment: AiMoment }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (moment.paste === undefined) return;
    try {
      await navigator.clipboard.writeText(moment.paste);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard (an insecure context, a denied permission): the text is on screen to select.
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-primary bg-primary-weak px-3 py-2 text-sm text-default">
      <p className="flex items-center gap-1.5 font-semibold text-primary">
        <Icon name="sparkles" size={4} />
        AI moment — show it live
      </p>
      <p>{moment.doThis}</p>
      {moment.paste !== undefined && (
        <div className="flex flex-col gap-1">
          <p className="max-h-24 select-all overflow-y-auto rounded-sm border border-default bg-default px-2 py-1 text-xs leading-relaxed">
            {moment.paste}
          </p>
          <Button variant="secondary" size="sm" className="self-start" onClick={() => void copy()}>
            <Icon name={copied ? "check" : "clipboard-check"} size={4} />
            {copied ? "Copied" : "Copy text"}
          </Button>
        </div>
      )}
      <p className="text-secondary">
        <span className="font-medium text-default">Then: </span>
        {moment.expect}
      </p>
    </div>
  );
}
